// Send logs to the server's CloudWatch batch endpoint in production. In dev
// (`npm run start`) write to the browser console instead so engineers can
// read messages live without leaking through the JWT-gated batch route. Set
// REACT_APP_REMOTE_LOGGING=true locally to exercise the batch path.
const useRemoteLogger = process.env.NODE_ENV === 'production' || process.env.REACT_APP_REMOTE_LOGGING === 'true';

const LEVELS = ['debug', 'info', 'warn', 'error'];

const FLUSH_INTERVAL_MS = 10_000;
const FLUSH_ENTRY_THRESHOLD = 100;
const FLUSH_BYTES_THRESHOLD = 256 * 1024;
const MAX_BUFFER_ENTRIES = 2000;
const MAX_MESSAGE_CHARS = 8192;
const MAX_RETRIES = 3;
const INITIAL_BACKOFF_MS = 1000;
const MAX_BACKOFF_MS = 30_000;

let orgId = null;
let authHeader = null;
let buffer = [];
let bufferBytes = 0;
let flushTimer = null;
let isFlushing = false;
let queuedFlush = false;
let lifecycleInstalled = false;

const ENDPOINT = '/api/client-logs/batch';

// Fetch keepalive bodies are capped at 64 KiB per-origin by the spec, so
// pagehide/visibilitychange flushes must be chunked under that.
const KEEPALIVE_CHUNK_BYTES = 60 * 1024;
const ENVELOPE_BYTES = '{"entries":[]}'.length;

const textEncoder = typeof TextEncoder !== 'undefined' ? new TextEncoder() : null;

function utf8ByteLength(s) {
    if (textEncoder !== null) {
        return textEncoder.encode(s).length;
    }
    return s.length;
}

function entryByteSize(entry) {
    return JSON.stringify(entry).length;
}

function chunkForKeepalive(entries) {
    const { chunks, current } = entries.reduce(
        (acc, entry) => {
            const entryBytes = utf8ByteLength(JSON.stringify(entry)) + (acc.current.length > 0 ? 1 : 0); // comma separator
            if (acc.current.length > 0 && acc.currentBytes + entryBytes > KEEPALIVE_CHUNK_BYTES) {
                acc.chunks.push(acc.current);
                return { chunks: acc.chunks, current: [entry], currentBytes: ENVELOPE_BYTES + entryBytes };
            }
            return {
                chunks: acc.chunks,
                current: [...acc.current, entry],
                currentBytes: acc.currentBytes + entryBytes
            };
        },
        { chunks: [], current: [], currentBytes: ENVELOPE_BYTES }
    );
    if (current.length > 0) {
        chunks.push(current);
    }
    return chunks;
}

function cancelScheduledFlush() {
    if (flushTimer !== null) {
        clearTimeout(flushTimer);
        flushTimer = null;
    }
}

async function postOnce(entries, auth, useKeepalive) {
    try {
        const res = await fetch(ENDPOINT, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: auth
            },
            body: JSON.stringify({ entries }),
            keepalive: useKeepalive
        });
        return { ok: res.ok, status: res.status };
    } catch {
        return { ok: false, status: 0 };
    }
}

function planNextAttempt(results) {
    return results.flatMap(({ batch, res }) => {
        if (res.ok) {
            return [];
        }
        if (res.status === 413 && batch.length > 1) {
            const mid = Math.floor(batch.length / 2);
            return [batch.slice(0, mid), batch.slice(mid)];
        }
        if (res.status === 0 || res.status >= 500) {
            return [batch];
        }
        return [];
    });
}

async function attemptBatches(pending, attempt, auth, useKeepalive) {
    if (pending.length === 0 || attempt > MAX_RETRIES) {
        return;
    }
    const results = await Promise.all(pending.map((batch) => postOnce(batch, auth, useKeepalive).then((res) => ({ batch, res }))));
    const next = planNextAttempt(results);
    if (next.length === 0 || useKeepalive) {
        return;
    }
    const delay = Math.min(MAX_BACKOFF_MS, INITIAL_BACKOFF_MS * 2 ** attempt);
    await new Promise((resolve) => {
        setTimeout(resolve, delay);
    });
    await attemptBatches(next, attempt + 1, auth, useKeepalive);
}

async function sendBatch(initialBatch, auth, useKeepalive) {
    await attemptBatches([initialBatch], 0, auth, useKeepalive);
}

async function flush({ useKeepalive = false } = {}) {
    if (isFlushing) {
        queuedFlush = true;
        return;
    }
    if (orgId === null || authHeader === null || buffer.length === 0) {
        return;
    }

    cancelScheduledFlush();
    isFlushing = true;
    const batch = buffer;
    buffer = [];
    bufferBytes = 0;

    try {
        if (useKeepalive) {
            const chunks = chunkForKeepalive(batch);
            await Promise.all(chunks.map((chunk) => postOnce(chunk, authHeader, true)));
        } else {
            await sendBatch(batch, authHeader, false);
        }
    } catch (err) {
        console.warn('client-logger: flush failed, dropping batch', err);
    } finally {
        isFlushing = false;
        if (queuedFlush) {
            queuedFlush = false;
            flush().catch(() => {});
        }
    }
}

function scheduleFlush() {
    if (flushTimer !== null) {
        return;
    }
    flushTimer = setTimeout(() => {
        flushTimer = null;
        flush().catch(() => {});
    }, FLUSH_INTERVAL_MS);
}

function enqueue(level, message, context) {
    if (!LEVELS.includes(level)) {
        return;
    }
    let safeMessage = typeof message === 'string' ? message : String(message);
    if (safeMessage.length > MAX_MESSAGE_CHARS) {
        safeMessage = safeMessage.slice(0, MAX_MESSAGE_CHARS);
    }
    const entry = {
        timestamp: Date.now(),
        level,
        message: safeMessage
    };
    if (context && typeof context === 'object') {
        entry.context = context;
    }

    while (buffer.length >= MAX_BUFFER_ENTRIES) {
        const dropped = buffer.shift();
        bufferBytes -= entryByteSize(dropped);
    }
    buffer.push(entry);
    bufferBytes += entryByteSize(entry);

    if (level === 'error' || buffer.length >= FLUSH_ENTRY_THRESHOLD || bufferBytes >= FLUSH_BYTES_THRESHOLD) {
        flush().catch(() => {});
    } else {
        scheduleFlush();
    }
}

function installLifecycleListeners() {
    if (lifecycleInstalled || typeof window === 'undefined') {
        return;
    }
    lifecycleInstalled = true;

    const flushOnHide = () => {
        if (buffer.length === 0) {
            return;
        }
        flush({ useKeepalive: true }).catch(() => {});
    };

    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') {
            flushOnHide();
        }
    });
    window.addEventListener('pagehide', flushOnHide);
}

function drainIfReady() {
    if (orgId !== null && authHeader !== null && buffer.length > 0) {
        flush().catch(() => {});
    }
}

/**
 * Bind the active org and install pagehide/visibilitychange flush listeners.
 * Call after the user's org is known (typically from the JWT context).
 * @param {number} orgIdValue
 */
export function initClientLogger(orgIdValue) {
    orgId = orgIdValue;
    installLifecycleListeners();
    drainIfReady();
}

/**
 * Set or clear the Authorization header used for log batch requests.
 * Pass `null` on logout.
 * @param {string | null} bearerHeader Full header value, e.g. `Bearer <token>`.
 */
export function setClientLoggerToken(bearerHeader) {
    authHeader = bearerHeader;
    drainIfReady();
}

/**
 * Drop org, token, and any buffered entries. Called on logout.
 */
export function resetClientLogger() {
    orgId = null;
    authHeader = null;
    buffer = [];
    bufferBytes = 0;
    cancelScheduledFlush();
}

/**
 * @typedef {Object} ClientLogger
 * @property {(message: string, context?: Record<string, unknown>) => void} debug
 * @property {(message: string, context?: Record<string, unknown>) => void} info
 * @property {(message: string, context?: Record<string, unknown>) => void} warn
 * @property {(message: string, context?: Record<string, unknown>) => void} error
 * @property {() => Promise<void>} flush Force-send buffered entries now.
 */

/** @type {ClientLogger} */
const remoteLogger = {
    debug: (message, context) => enqueue('debug', message, context),
    info: (message, context) => enqueue('info', message, context),
    warn: (message, context) => enqueue('warn', message, context),
    error: (message, context) => enqueue('error', message, context),
    flush: () => flush()
};

/** @type {ClientLogger} */
const consoleLogger = {
    debug: (message, context) => (context ? console.debug(message, context) : console.debug(message)),
    info: (message, context) => (context ? console.info(message, context) : console.info(message)),
    warn: (message, context) => (context ? console.warn(message, context) : console.warn(message)),
    error: (message, context) => (context ? console.error(message, context) : console.error(message)),
    flush: () => Promise.resolve()
};

const logger = useRemoteLogger ? remoteLogger : consoleLogger;

export default logger;
